import { env } from '@/config/env';

import { ModuleData, Session, Worksheet } from './types';

export const getWorksheetScore = (worksheet?: Worksheet): number => {
  if (!worksheet) {
    return 0;
  }
  if (worksheet.total_marks && worksheet.aggregated_score) {
    const totalMarks = Number(worksheet.total_marks);
    const percentage = (worksheet.aggregated_score / totalMarks) * 100;
    return Math.round(percentage);
  }
  return Math.round(worksheet.aggregated_score ?? 0);
};

export const openLiveSessionInOldApp = (variantId: string) => {
  const url = `${env.OLD_APP_URL}/live-session?variant_id=${variantId}`;
  window.open(url, '_blank');
};

/** Sessions of one module that were delivered in a single batch. */
export interface BatchSessionGroup {
  batchId?: string;
  batchName?: string;
  sessions: Session[];
  /** Palette slot for the pill; only meaningful for batches the student left. */
  colorIndex: number;
}

/** A module and its sessions split into one group per batch. */
export interface ModuleBatchGroups {
  moduleData: ModuleData;
  batchGroups: BatchSessionGroup[];
  /** Palette slot for this module's cards, shared across both sections. */
  colorIndex: number;
}

export interface BatchSegregatedModules {
  currentModules: ModuleBatchGroups[];
  previousModules: ModuleBatchGroups[];
  /** True once at least one session came from a batch the student has left. */
  hasBatchChange: boolean;
  /** Distinct modules coloured, so later sections continue the rotation. */
  coloredModuleCount: number;
}

const SESSIONS_WITHOUT_BATCH = '__no_batch__';

const latestSessionTimestamp = (sessions: Session[]) =>
  Math.max(0, ...sessions.map(session => session.date?.timestamp ?? 0));

const groupSessionsByBatch = (sessions: Session[]): BatchSessionGroup[] => {
  const groups = new Map<string, BatchSessionGroup>();

  for (const session of sessions) {
    const key = session.batch_id ?? SESSIONS_WITHOUT_BATCH;
    const group = groups.get(key);
    if (group) {
      group.sessions.push(session);
      group.batchName ??= session.batch_name;
    } else {
      groups.set(key, {
        batchId: session.batch_id,
        batchName: session.batch_name,
        sessions: [session],
        colorIndex: 0,
      });
    }
  }

  return [...groups.values()].sort(
    (a, b) =>
      latestSessionTimestamp(b.sessions) - latestSessionTimestamp(a.sessions)
  );
};

const byModuleNumberDesc = (a: ModuleData, b: ModuleData) =>
  Number.parseInt(b.module.number, 10) - Number.parseInt(a.module.number, 10);

/** Ongoing modules first, then highest module number → lowest. */
const byOngoingThenNumberDesc = (a: ModuleData, b: ModuleData) => {
  const aOngoing = a.module.status === 'PAID_ONGOING' ? 0 : 1;
  const bOngoing = b.module.status === 'PAID_ONGOING' ? 0 : 1;
  if (aOngoing !== bOngoing) {
    return aOngoing - bOngoing;
  }
  return byModuleNumberDesc(a, b);
};

const nextColorIndex = (colors: Map<string, number>, key: string) => {
  const existing = colors.get(key);
  if (existing !== undefined) {
    return existing;
  }
  colors.set(key, colors.size);
  return colors.size - 1;
};

/**
 * Hands out one palette slot per module and one per left batch, walking the
 * sections in the order the page renders them. A module taught across a batch
 * change keeps its colour in both halves, and an old batch keeps its pill
 * colour under every module it shows up in.
 *
 * Returns how many modules were coloured, so sections rendered after these
 * (on-demand) can continue the rotation instead of restarting it.
 */
const assignColorIndexes = (
  currentModules: ModuleBatchGroups[],
  previousModules: ModuleBatchGroups[]
): number => {
  const moduleColors = new Map<string, number>();
  const batchColors = new Map<string, number>();

  for (const moduleGroup of [...currentModules, ...previousModules]) {
    moduleGroup.colorIndex = nextColorIndex(
      moduleColors,
      moduleGroup.moduleData.module.id
    );
  }

  for (const { batchGroups } of previousModules) {
    for (const group of batchGroups) {
      group.colorIndex = nextColorIndex(batchColors, group.batchId ?? '');
    }
  }

  return moduleColors.size;
};

/**
 * Splits modules into the student's current batch and the batches they have
 * left, keeping a module in both halves when the same module was taught across
 * a batch change.
 *
 * Within each half, modules are ordered ongoing first, then by descending
 * module number (including when the API omits batch fields and everything
 * stays in the current section).
 *
 * A session counts as current unless it carries a `batch_id` that differs from
 * `currentBatchId`, so the previous section stays empty while the modules API
 * omits `batch_id`.
 */
export const segregateModulesByBatch = (
  modules: ModuleData[],
  currentBatchId?: string
): BatchSegregatedModules => {
  const currentModules: ModuleBatchGroups[] = [];
  const previousModules: ModuleBatchGroups[] = [];

  const isCurrentBatchSession = (session: Session) =>
    !currentBatchId || !session.batch_id || session.batch_id === currentBatchId;

  for (const moduleData of [...modules].sort(byOngoingThenNumberDesc)) {
    const sessions = moduleData.sessions ?? [];
    if (sessions.length === 0) {
      continue;
    }

    const currentSessions = sessions.filter(session =>
      isCurrentBatchSession(session)
    );
    const previousSessions = sessions.filter(
      session => !isCurrentBatchSession(session)
    );

    if (currentSessions.length > 0) {
      currentModules.push({
        moduleData,
        batchGroups: groupSessionsByBatch(currentSessions),
        colorIndex: 0,
      });
    }
    if (previousSessions.length > 0) {
      previousModules.push({
        moduleData,
        batchGroups: groupSessionsByBatch(previousSessions),
        colorIndex: 0,
      });
    }
  }

  return {
    currentModules,
    previousModules,
    hasBatchChange: previousModules.length > 0,
    coloredModuleCount: assignColorIndexes(currentModules, previousModules),
  };
};
